import React, { useMemo, useCallback } from "react";
import type { GroupingContext } from "@/components/shared/GroupingWrapper/types";
import type { GroupHeader as GroupHeaderModel } from "@/lib/utils/crm/grouping-strategies/types";
import { GroupHeader } from "./GroupHeader";
import { GroupHeaderContextMenu } from "./GroupHeaderContextMenu";

// Separate component for group header rows to allow hooks at top level
export interface GroupHeaderRowProps {
  groupItem: GroupHeaderModel;
  groupingContext?: GroupingContext;
}

export const GroupHeaderRow = React.memo(function GroupHeaderRow({
  groupItem,
  groupingContext,
}: GroupHeaderRowProps) {
  const level = groupItem.level || 1;
  const isPrimary = level === 1;
  const isSecondary = level === 2;

  // Memoize group expansion state calculation
  const isExpanded = useMemo(() => {
    if (isPrimary) {
      // Check if this is dual grouping mode
      const isDualGrouping = Boolean(
        groupingContext?.dualGrouping?.primary && groupingContext?.dualGrouping?.secondary
      );
      if (isDualGrouping) {
        // In dual grouping, check nestedExpandedGroups
        const nestedGroups = groupingContext?.nestedExpandedGroups?.get(groupItem.groupKey);
        return nestedGroups !== undefined && nestedGroups.size > 0;
      }
      // In single grouping, check expandedGroups
      return groupingContext?.expandedGroups.has(groupItem.groupKey) || false;
    }
    if (isSecondary) {
      // For secondary groups, check nested expansion
      return (
        groupingContext?.nestedExpandedGroups
          ?.get(groupItem.parentKey || "")
          ?.has(groupItem.groupKey) || false
      );
    }
    return false;
  }, [isPrimary, isSecondary, groupingContext, groupItem.groupKey, groupItem.parentKey]);

  // Memoize toggle handler to prevent recreation on every render
  const handleToggle = useCallback(() => {
    if (isPrimary) {
      groupingContext?.onToggleGroup(groupItem.groupKey);
    } else if (isSecondary) {
      // Toggle nested group
      groupingContext?.onToggleNestedGroup?.(groupItem.parentKey ?? "", groupItem.groupKey);
    }
  }, [isPrimary, isSecondary, groupingContext, groupItem.groupKey, groupItem.parentKey]);

  return (
    <GroupHeaderContextMenu
      groupingContext={groupingContext}
      isExpanded={isExpanded}
      onToggle={handleToggle}
    >
      <GroupHeader
        groupItem={groupItem}
        isPrimary={isPrimary}
        isSecondary={isSecondary}
        isExpanded={isExpanded}
        onToggle={handleToggle}
      />
    </GroupHeaderContextMenu>
  );
});
